import { useRef, useState, useEffect } from "react";
import { API } from "@/lib/api";
import { useCity } from "@/context/CityContext";
import { Sparkles, Send, User } from "lucide-react";

const EVENT_TYPES = ["Marriage", "Mehandi", "Reception", "Birthday", "Baby Shower", "Photography"];
const BUDGETS = ["Under ₹1L", "₹1L – ₹3L", "₹3L – ₹7L", "₹7L+"];
const SUGGESTIONS = [
  "Plan a 300-guest marriage in Bengaluru under ₹5 lakh",
  "I need decor, DJ and catering for a reception",
  "Budget baby shower ideas with vendors",
];

export default function AIPlanner() {
  const { city } = useCity();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [eventType, setEventType] = useState("");
  const [budget, setBudget] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/plan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: msg,
          session_id: sessionRef.current,
          event_type: eventType,
          budget,
          city: city || "",
        }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (data === "[DONE]") continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.session_id) sessionRef.current = parsed.session_id;
            if (parsed.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content: copy[copy.length - 1].content + parsed.delta,
                };
                return copy;
              });
            }
            if (parsed.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: parsed.error };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I'm unavailable right now." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] md:h-[calc(100vh-4rem)]">
      <div className="px-4 md:px-8 pt-6 pb-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-primary/10 grid place-items-center">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="font-display font-extrabold text-2xl tracking-tight">AI Event Planner</h1>
            <p className="text-sm text-muted-foreground">
              Tell me your event & budget — I'll recommend vendors and a plan.
            </p>
          </div>
        </div>

        <div className="flex gap-2 mt-4 overflow-x-auto no-scrollbar">
          {EVENT_TYPES.map((t) => (
            <button
              key={t}
              data-testid={`ai-event-${t}`}
              onClick={() => setEventType(eventType === t ? "" : t)}
              className={`shrink-0 px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${
                eventType === t ? "bg-primary text-white border-primary" : "border-stone-300 text-muted-foreground"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="flex gap-2 mt-2 overflow-x-auto no-scrollbar">
          {BUDGETS.map((b) => (
            <button
              key={b}
              data-testid={`ai-budget-${b}`}
              onClick={() => setBudget(budget === b ? "" : b)}
              className={`shrink-0 px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${
                budget === b ? "bg-secondary/20 border-secondary text-secondary-foreground" : "border-stone-200 text-muted-foreground"
              }`}
            >
              {b}
            </button>
          ))}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 md:px-8 pb-4 space-y-4">
        {messages.length === 0 && (
          <div className="space-y-2 pt-4">
            <p className="text-xs text-muted-foreground overline">Try asking</p>
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                data-testid="ai-suggestion"
                onClick={() => send(s)}
                className="block w-full text-left bg-card border border-stone-200 rounded-xl px-4 py-3 text-sm hover:border-primary transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        )}
        {messages.map((m, idx) => (
          <div key={idx} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
            {m.role === "assistant" && (
              <div className="h-8 w-8 rounded-lg bg-primary/10 grid place-items-center shrink-0">
                <Sparkles className="h-4 w-4 text-primary" />
              </div>
            )}
            <div
              data-testid={`ai-msg-${m.role}`}
              className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${
                m.role === "user"
                  ? "bg-primary text-white rounded-br-sm"
                  : "bg-card border border-stone-200 rounded-bl-sm"
              }`}
            >
              {m.content || (streaming && idx === messages.length - 1 ? "Thinking..." : "")}
            </div>
            {m.role === "user" && (
              <div className="h-8 w-8 rounded-lg bg-muted grid place-items-center shrink-0">
                <User className="h-4 w-4" />
              </div>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="glass border-t border-stone-200/60 px-4 md:px-8 py-3">
        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="flex items-center gap-2 max-w-3xl mx-auto"
        >
          <input
            data-testid="ai-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Describe your event..."
            className="flex-1 h-12 rounded-full border border-stone-200 bg-card px-5 text-sm outline-none focus:border-primary"
          />
          <button
            data-testid="ai-send-btn"
            type="submit"
            disabled={streaming}
            className="h-12 w-12 grid place-items-center bg-primary text-white rounded-full disabled:opacity-60"
          >
            <Send className="h-5 w-5" />
          </button>
        </form>
      </div>
    </div>
  );
}
